import { FaroTextField } from "@components/common/faro-text-field/faro-text-field";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { assert } from "@faro-lotv/foundation";
import { Box } from "@mui/material";
import { useAccountSettingsLayout } from "@pages/account-settings/account-settings-layout/account-settings-layout-utils";
import { useUserContext } from "@pages/account-settings/user-provider";
import { useAppDispatch } from "@store/store-helper";
import { updateCurrentUserProfile } from "@store/user/user-slice-thunk";
import { UserProfile } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useCallback } from "react";

interface Props {
  /** Whether the changes are made for first name or last name */
  field: "firstName" | "lastName";
}

/** Change the account name */
export function AccountChangeName({ field }: Props): JSX.Element {
  const { user } = useUserContext();
  assert(user, "User has to be defined to change name");
  const { accountSettingsFontSize } = useAccountSettingsLayout();
  const { trackEvent } = useTrackEvent();
  const dispatch = useAppDispatch();
  const { handleErrorWithToast } = useErrorContext();

  const onChangeDisplayName = useCallback(
    async ({
      newFirstName,
      newLastName,
    }: {
      newFirstName?: string;
      newLastName?: string;
    }): Promise<string> => {
      trackEvent({
        name: UserProfile.updateProfile,
        props: { attribute: { field } },
      });

      newFirstName = (newFirstName ?? "")?.trim();
      newLastName = (newLastName ?? "")?.trim();
      try {
        await dispatch(
          updateCurrentUserProfile({
            content: "metaData",
            entity: {
              // Make sure to trim the value to avoid empty spaces
              name: newFirstName,
              lastName: newLastName,
              mailAddress: user.email,
              profileImageUrl: user.thumbnailUrl,
            },
          })
        ).unwrap();

        return field === "firstName" ? newFirstName : newLastName;
      } catch (error) {
        handleErrorWithToast({
          id: `changeDisplayName-${Date.now().toString()}`,
          title: `Could not change ${field}`,
          error,
        });
        return field === "firstName" ? user.firstName : user.lastName ?? "";
      }
    },
    [dispatch, field, handleErrorWithToast, trackEvent, user]
  );

  return (
    <Box component="div" width="100%">
      <FaroTextField
        initialValue={
          field === "firstName" ? user.firstName : user.lastName ?? ""
        }
        onConfirmed={(value) =>
          onChangeDisplayName({
            newFirstName: field === "firstName" ? value : user.firstName,
            newLastName: field === "firstName" ? user.lastName ?? "" : value,
          })
        }
        isFullWidth={true}
        faroVariant="row"
        isReadOnly={false}
        minInputLength={1}
        fontSize={accountSettingsFontSize}
      />
    </Box>
  );
}
